import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { logOfferUnavailable } from '../utils/logOfferUnavailable';
import {
  OFFER_SOLD_OUT_REASON,
  buildOfferUnavailableRoute,
} from '../utils/offerUnavailableRoute';
import type { OfferAvailabilityStatus, OfferPlanAvailability } from '../types';

type Query = Record<string, string | string[] | undefined>;

/**
 * FIN-8119 (AC 3 de "Oferta esgotada"): a página de pagamento de uma oferta
 * esgotada não exibe o pagamento. Cobre quem chega direto em `/payment` ou dá
 * refresh depois que o produto esgotou: a disponibilidade vem do SSR, relida no
 * BFF a cada carregamento. O lead já existe nesse ponto, então vai direto para
 * a página de obrigado. Devolve true enquanto redireciona; a página não
 * renderiza o pagamento.
 * `freshAvailability` é a disponibilidade da v2 que `/payment` busca de novo no
 * cliente: prevalece sobre a do contexto (SSR), que fica velha quando o
 * produto esgota entre o lead e o pagamento numa navegação sem refresh.
 */
export function useSoldOutOfferRedirect({
  routerQuery,
  offerAvailability,
  freshAvailability,
  productCode,
  business,
}: {
  routerQuery: Query;
  offerAvailability: OfferPlanAvailability | null | undefined;
  freshAvailability?: OfferAvailabilityStatus | null;
  productCode?: string | null;
  business?: string | null;
}): boolean {
  const router = useRouter();
  const hasRedirectedRef = useRef(false);

  const availability = freshAvailability ?? offerAvailability?.availability;
  const isSoldOut = !!routerQuery.oferta && availability === 'SOLD_OUT';

  useEffect(() => {
    if (!isSoldOut || !router.isReady || hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;

    logOfferUnavailable({
      operation: 'payment-page-diversion',
      planId: routerQuery.oferta as string,
      productCode,
      reason: 'sold_out',
      business,
    });

    router.replace(
      buildOfferUnavailableRoute(routerQuery, OFFER_SOLD_OUT_REASON)
    );
  }, [isSoldOut, router, routerQuery, productCode, business]);

  return isSoldOut;
}
